import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLists } from '../contexts/ListsContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DesignationIcon from '../components/DesignationIcon';
import ReactivatePremiumModal from '../components/ReactivatePremiumModal';
import api from '../services/api';
import { trackPlaceSave } from '../services/analytics';
import '../styles/SavePlacePage.css';

const DESIGNATIONS = [
  { value: 'gem', label: 'Gem' },
  { value: 'highly-recommended', label: 'Highly Recommended' },
  { value: 'visited', label: 'Visited' },
  { value: 'avoid', label: 'Avoid' },
  { value: 'want-to-go', label: 'Want to Go' },
];

// Social links for users to link to their own content about this place
const SOCIAL_LINK_TYPES = [
  { value: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...', icon: 'tiktok' },
  { value: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...', icon: 'youtube' },
  { value: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...', icon: 'instagram' },
  { value: 'website', label: 'Website/Blog', placeholder: 'https://...', icon: 'globe' },
];

// Validate social link URL format and domain
const validateSocialLink = (type, url) => {
  if (!url || !url.trim()) return { valid: true }; // Empty is valid (optional)

  const trimmedUrl = url.trim();

  // Check valid URL format
  try {
    const parsed = new URL(trimmedUrl);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return { valid: false, error: 'URL must start with http:// or https://' };
    }
  } catch {
    return { valid: false, error: 'Invalid URL format' };
  }

  // Check blocked domains (URL shorteners, etc.)
  const blockedPatterns = [/bit\.ly/i, /tinyurl\.com/i, /t\.co/i, /goo\.gl/i];
  if (blockedPatterns.some(p => p.test(trimmedUrl))) {
    return { valid: false, error: 'URL shorteners are not allowed' };
  }

  // Check domain for specific platforms
  const domainPatterns = {
    instagram: [/instagram\.com/i, /instagr\.am/i],
    tiktok: [/tiktok\.com/i],
    youtube: [/youtube\.com/i, /youtu\.be/i],
    website: null, // Any domain allowed
  };

  const patterns = domainPatterns[type];
  if (patterns !== null && !patterns.some(p => p.test(trimmedUrl))) {
    const typeLabel = SOCIAL_LINK_TYPES.find(t => t.value === type)?.label || type;
    return { valid: false, error: `URL must be a valid ${typeLabel} link` };
  }

  return { valid: true };
};

function SavePlace() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lists, collaborationLists, addPlaceToMultipleLists, deletePlaceFromMultipleLists, fetchLists, fetchCollaborationLists } = useLists();
  const { getDesignation, setDesignation: saveDesignation, deleteDesignation, fetchDesignations, ensureDesignationsLoaded } = useUserDesignations();
  const { showAlert, showConfirm, showToast } = useNotificationModal();
  const { isPremium, canAccess } = useSubscription();
  const canAddSocialLinks = canAccess('placeSocialLinks');

  // Get place data from location state
  const place = location.state?.place;
  const existingListInfo = location.state?.existingListInfo;
  const returnPath = location.state?.returnPath || '/lists';
  const returnState = location.state?.returnState;
  const sourceListInfo = location.state?.sourceListInfo; // For tracking saves from other users' lists

  const [designation, setDesignation] = useState('want-to-go');
  const [designationSelectOpen, setDesignationSelectOpen] = useState(false);
  const [avoidReason, setAvoidReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [socialLinks, setSocialLinks] = useState({}); // { instagram: 'url', tiktok: 'url', ... }
  const [socialLinkErrors, setSocialLinkErrors] = useState({}); // { instagram: 'error msg', ... }
  const [selectedLists, setSelectedLists] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const designationSelectRef = useRef(null);

  // Get user's existing designation for this place
  const existingDesignation = place?.placeId ? getDesignation(place.placeId) : null;

  // Determine if this is the first time adding this place (no existing designation)
  const isFirstTime = !existingDesignation;

  // Check if user has inactive social links (had premium with links but premium expired)
  const hasInactiveSocialLinks = existingDesignation?.hasInactiveSocialLinks === true;
  const inactiveSocialLinksCount = existingDesignation?.inactiveSocialLinksCount || 0;

  // Combine owned lists and collaboration lists
  const allLists = useMemo(() => {
    // Owned lists: mark as collaboration if they have collaborators
    const owned = lists.map(list => ({
      ...list,
      isCollaboration: list.collaborators && list.collaborators.length > 0
    }));
    const collab = collaborationLists.map(list => ({ ...list, isCollaboration: true }));
    return [...owned, ...collab].sort((a, b) => a.title.localeCompare(b.title));
  }, [lists, collaborationLists]);

  // Ensure user designations are loaded (for displaying existing designation)
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  useEffect(() => {
    if (place) {
      // Initialize selected lists based on existing list info
      const initialSelected = {};

      allLists.forEach(list => {
        const existingInfo = existingListInfo?.find(info => info.listId === list.id);
        initialSelected[list.id] = !!existingInfo;
      });

      setSelectedLists(initialSelected);

      // Set designation from user's global designation or default
      if (existingDesignation) {
        setDesignation(existingDesignation.badge || 'want-to-go');
        setAvoidReason(existingDesignation.avoidReason || '');
        setNotes(existingDesignation.notes || '');
        setIsPrivate(existingDesignation.isPrivate || false);

        // Initialize social links from existing designation
        const existingLinks = {};
        if (existingDesignation.socialLinks && Array.isArray(existingDesignation.socialLinks)) {
          existingDesignation.socialLinks.forEach(link => {
            if (link.type && link.url) {
              existingLinks[link.type] = link.url;
            }
          });
        }
        setSocialLinks(existingLinks);
        setSocialLinkErrors({});
      } else {
        setDesignation('want-to-go');
        setAvoidReason('');
        setNotes('');
        setIsPrivate(false);
        setSocialLinks({});
        setSocialLinkErrors({});
      }

      setError(null);
    }
  }, [place, allLists, existingListInfo, existingDesignation]);

  // Redirect if no place data
  useEffect(() => {
    if (!place) {
      navigate(returnPath);
    }
  }, [place, navigate, returnPath]);

  // Close designation dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (designationSelectRef.current && !designationSelectRef.current.contains(event.target)) {
        setDesignationSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleListToggle = (listId) => {
    setSelectedLists(prev => ({
      ...prev,
      [listId]: !prev[listId]
    }));
  };

  const handleDesignationChange = (newDesignation) => {
    setDesignation(newDesignation);
    if (newDesignation !== 'avoid') {
      setAvoidReason('');
    }
  };

  const handleSocialLinkChange = (type, url) => {
    setSocialLinks(prev => ({
      ...prev,
      [type]: url,
    }));

    // Validate on change
    const validation = validateSocialLink(type, url);
    setSocialLinkErrors(prev => ({
      ...prev,
      [type]: validation.valid ? null : validation.error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!designation) {
      setError('Please select a designation');
      return;
    }

    if (designation === 'avoid' && !avoidReason) {
      setError('Please select a reason to avoid');
      return;
    }

    // Check if at least one list is selected
    const hasSelectedList = Object.values(selectedLists).some(v => v);
    if (!hasSelectedList) {
      setError('Please select at least one list');
      return;
    }

    // Validate all social links
    const linkErrors = {};
    let hasLinkErrors = false;
    SOCIAL_LINK_TYPES.forEach(({ value: type }) => {
      const url = socialLinks[type];
      if (url && url.trim()) {
        const validation = validateSocialLink(type, url);
        if (!validation.valid) {
          linkErrors[type] = validation.error;
          hasLinkErrors = true;
        }
      }
    });

    if (hasLinkErrors) {
      setSocialLinkErrors(linkErrors);
      setError('Please fix the social link errors');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const existingListIds = existingListInfo?.map(info => info.listId) || [];

      // Collect lists to add to and remove from (only owned lists for now)
      const listsToAdd = [];
      const listsToRemove = [];

      for (const list of allLists) {
        const isSelected = selectedLists[list.id];
        const wasSelected = existingListIds.includes(list.id);

        if (isSelected && !wasSelected) {
          listsToAdd.push(list.id);
        } else if (!isSelected && wasSelected) {
          listsToRemove.push(list.id);
        }
      }

      // Prepare the place data for adding to lists (no designation data - that's stored separately)
      const now = new Date().toISOString();
      const newPlace = {
        ...place,
        savedAt: now,
        photoUrlSavedAt: place.photoUrl ? (place.photoUrlSavedAt || now) : null,
        openingHoursSavedAt: place.openingHours ? (place.openingHoursSavedAt || now) : null,
      };

      // Build social links array (only include non-empty links)
      const socialLinksArray = SOCIAL_LINK_TYPES
        .filter(({ value: type }) => socialLinks[type] && socialLinks[type].trim())
        .map(({ value: type }) => ({
          type,
          url: socialLinks[type].trim(),
          addedAt: new Date().toISOString(),
        }));

      // Save the user's designation first (always, even if unchanged - to handle notes)
      // This is done first because it's the most important operation
      await saveDesignation(place.placeId, {
        badge: designation,
        avoidReason: designation === 'avoid' ? avoidReason : null,
        notes: notes || null,
        isPrivate,
        socialLinks: socialLinksArray.length > 0 ? socialLinksArray : null,
      });

      // Run list operations in parallel (these can fail independently)
      const listOperations = [];
      const operationResults = { addFailed: false, removeFailed: false };

      // Add to new lists
      if (listsToAdd.length > 0) {
        listOperations.push(
          addPlaceToMultipleLists(newPlace, listsToAdd)
            .catch(err => {
              console.error('Failed to add to lists:', err);
              operationResults.addFailed = true;
            })
        );
      }

      // Remove from unselected lists
      if (listsToRemove.length > 0) {
        listOperations.push(
          deletePlaceFromMultipleLists(place.placeId, listsToRemove)
            .catch(err => {
              console.error('Failed to remove from lists:', err);
              operationResults.removeFailed = true;
            })
        );
      }

      // Wait for list operations to complete
      await Promise.all(listOperations);

      // Check if any list operations failed
      if (operationResults.addFailed || operationResults.removeFailed) {
        // Designation was saved, but list operations had issues
        showAlert('Designation saved, but some list changes may not have been applied. Please try again.', { variant: 'warning' });
        handleClose();
        return;
      }

      // For premium users, enrich the place with photos, opening hours, etc.
      // Only if we actually added to a new list (fire-and-forget)
      if (isPremium && listsToAdd.length > 0 && place.placeId) {
        api.enrichPlace(place.placeId).catch((err) => {
          console.error('Failed to enrich place (non-blocking):', err);
        });
      }

      // Track place save analytics if this is a new save from another user's list
      // Only track if: 1) adding to at least one new list, 2) have source list info, 3) first time saving this place
      if (listsToAdd.length > 0 && sourceListInfo?.sourceListId && sourceListInfo?.sourceListOwnerId && isFirstTime) {
        trackPlaceSave(sourceListInfo.sourceListId, sourceListInfo.sourceListOwnerId, place.placeId);
      }

      showToast('Place saved successfully!');
      handleClose();
    } catch (err) {
      console.error('Failed to save place:', err);
      setError(err.message || 'Failed to save place');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      `This will remove "${place.name}" from all your lists and delete your notes and designation. This cannot be undone.`,
      {
        title: 'Delete Place',
        confirmText: 'Delete',
        variant: 'error'
      }
    );

    if (!confirmed) return;

    setDeleting(true);
    setError(null);

    try {
      await deleteDesignation(place.placeId);

      // Refresh lists to reflect the changes
      await Promise.all([
        fetchLists(),
        fetchCollaborationLists(),
        fetchDesignations(),
      ]);

      showToast('Place deleted successfully');
      handleClose();
    } catch (err) {
      console.error('Failed to delete place:', err);
      setError(err.message || 'Failed to delete place');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    // If we have returnState (coming from PlaceDetails with state to restore), navigate back there
    // Use replace: true to restore the original history entry that was replaced when navigating here
    if (returnState && returnPath) {
      navigate(returnPath, { replace: true, state: returnState });
    } else if (returnPath && returnPath !== '/lists') {
      // If we have a specific return path (not the default), navigate there
      navigate(returnPath, { replace: true });
    } else {
      // Default: go back in browser history
      navigate(-1);
    }
  };

  if (!place) {
    return null;
  }

  return (
    <div className="save-place-page" onContextMenu={(e) => e.preventDefault()}>
      <div className="save-place-content">
        <div className="form-header">
          <button className="back-btn" onClick={handleClose} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="form-title">Manage Place</h1>
          <div className="header-spacer" />
        </div>

        <div className="save-place-info">
          <div className="place-name-display">{place.name}</div>
          <div className="place-address-display">{place.address}</div>
        </div>

        <form onSubmit={handleSubmit} className="save-place-form">
          {allLists.length > 0 ? (
            <>
              {/* Designation selector */}
              <div className="form-group">
                <label>Your Designation</label>
                <div
                  ref={designationSelectRef}
                  className={`designation-select-custom ${designationSelectOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="designation-select-trigger"
                    onClick={() => setDesignationSelectOpen(!designationSelectOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={designationSelectOpen}
                  >
                    <span className="designation-option-content">
                      <DesignationIcon designation={designation} size={16} />
                      <span>{DESIGNATIONS.find(d => d.value === designation)?.label || 'Select'}</span>
                    </span>
                    <svg className="designation-select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {designationSelectOpen && (
                    <ul className="designation-select-dropdown" role="listbox">
                      {DESIGNATIONS.map((d) => (
                        <li
                          key={d.value}
                          className={`designation-option ${designation === d.value ? 'selected' : ''}`}
                          onClick={() => {
                            handleDesignationChange(d.value);
                            setDesignationSelectOpen(false);
                          }}
                          role="option"
                          aria-selected={designation === d.value}
                        >
                          <DesignationIcon designation={d.value} size={16} />
                          <span>{d.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {!isFirstTime && (
                  <div className="designation-sync-note">
                    Your designation is the same across all lists
                  </div>
                )}
              </div>

              {designation === 'avoid' && (
                <div className="form-group">
                  <label>Reason to Avoid</label>
                  <select
                    className="form-select"
                    value={avoidReason}
                    onChange={(e) => setAvoidReason(e.target.value)}
                  >
                    <option value="">Select reason</option>
                    <option value="cost">Cost</option>
                    <option value="service">Service</option>
                    <option value="ambiance">Ambiance</option>
                    <option value="quality">Quality</option>
                    <option value="location">Location</option>
                    <option value="cleanliness">Cleanliness</option>
                  </select>
                </div>
              )}

              {/* Notes field */}
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add your thoughts, tips, or recommendations..."
                  rows="3"
                />
              </div>

              {/* Social Links - Premium only */}
              {canAddSocialLinks ? (
                <div className="form-group social-links-group">
                  <label>Social Links <span className="optional-label">(optional)</span></label>
                  <p className="form-hint">Link to your content about this place</p>
                  <div className="social-links-list">
                    {SOCIAL_LINK_TYPES.map(({ value: type, label, placeholder, icon }) => (
                      <div key={type} className={`social-link-input ${socialLinkErrors[type] ? 'has-error' : ''}`}>
                        <div className="social-link-icon">
                          {icon === 'instagram' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                            </svg>
                          )}
                          {icon === 'tiktok' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                          )}
                          {icon === 'youtube' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                              <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                            </svg>
                          )}
                          {icon === 'globe' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="2" y1="12" x2="22" y2="12"/>
                              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                            </svg>
                          )}
                        </div>
                        <input
                          type="url"
                          className="form-input social-link-url"
                          value={socialLinks[type] || ''}
                          onChange={(e) => handleSocialLinkChange(type, e.target.value)}
                          placeholder={placeholder}
                        />
                        {socialLinks[type] && (
                          <button
                            type="button"
                            className="social-link-clear"
                            onClick={() => handleSocialLinkChange(type, '')}
                            aria-label={`Clear ${label}`}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        )}
                        {socialLinkErrors[type] && (
                          <div className="social-link-error">{socialLinkErrors[type]}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : hasInactiveSocialLinks ? (
                <div className="form-group social-links-group premium-inactive-group">
                  <label>
                    Social Links
                    <svg className="inactive-lock-icon-label" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </label>
                  <div className="premium-inactive-content">
                    <div className="premium-inactive-indicator">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                      <span>{inactiveSocialLinksCount} {inactiveSocialLinksCount === 1 ? 'link' : 'links'} saved</span>
                    </div>
                    <p className="premium-inactive-text">Your links are saved but not visible to others.</p>
                    <button
                      type="button"
                      className="btn-reactivate-small"
                      onClick={() => setShowReactivateModal(true)}
                    >
                      Reactivate Premium
                    </button>
                  </div>
                </div>
              ) : (
                <div className="form-group social-links-group premium-locked">
                  <label>Social Links <span className="premium-badge">Premium</span></label>
                  <p className="form-hint">Link to your content about this place</p>
                  <button
                    type="button"
                    className="premium-upgrade-prompt"
                    onClick={() => navigate('/premium')}
                  >
                    Upgrade to add social links
                  </button>
                </div>
              )}

              {/* Privacy toggle */}
              <div className="form-group privacy-toggle-group">
                <div className="privacy-toggle-row">
                  <div className="privacy-toggle-icon">
                    {isPrivate ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                    )}
                  </div>
                  <span className="privacy-toggle-label-text">{isPrivate ? 'Private' : 'Public'}</span>
                  <label className="privacy-toggle-switch">
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p className="privacy-toggle-description">
                  {isPrivate
                    ? 'Your designation, notes, and social links won\'t be visible to other users'
                    : 'Your designation, notes, and social links can be seen by other users'
                  }
                </p>
              </div>

              {/* List selection with checkboxes */}
              <div className="form-group">
                <label>Your Lists</label>
                <div className="lists-checklist">
                  {allLists.map(list => {
                    // Check if this place was added by someone else on a collab list
                    const listItemInfo = existingListInfo?.find(info => info.listId === list.id);
                    const addedByOther = list.isCollaboration && listItemInfo?.addedBy && listItemInfo.addedBy !== list.userId;

                    return (
                      <label key={list.id} className="list-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedLists[list.id] || false}
                          onChange={() => handleListToggle(list.id)}
                        />
                        <span className="custom-checkbox"></span>
                        <span className="list-checkbox-name">
                          {list.title}
                          {list.isCollaboration && (
                            <span className="collab-indicator"> (Collaboration)</span>
                          )}
                          {addedByOther && listItemInfo?.addedByName && (
                            <span className="added-by-indicator"> - Added by {listItemInfo.addedByName}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="no-lists-message">
              <p>You don't have any lists yet. Create a list first to save places.</p>
              <button
                type="button"
                className="btn-create-list"
                onClick={() => navigate('/lists/new')}
              >
                Create a List
              </button>
            </div>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-actions">
            {allLists.length > 0 && (
              <button type="submit" className="btn-submit" disabled={loading || deleting}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <div className="secondary-actions">
              {!isFirstTime && (
                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={loading || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Place'}
                </button>
              )}
              <button type="button" className="btn-cancel" onClick={handleClose}>
                Cancel
              </button>
            </div>
          </div>
        </form>

        <ReactivatePremiumModal
          isOpen={showReactivateModal}
          onClose={() => setShowReactivateModal(false)}
          featureType="socialLinks"
        />
      </div>
    </div>
  );
}

export default SavePlace;
